/**
 * O complemento com destino por CCB (E177.F6, GUS-253, ADR-0130). Espelha
 * `apps/api/types/complemento_por_ccb.py` e as rotas de `apps/api/runtime/complemento_por_ccb.py`:
 *
 * - `GET /api/casos/{id}/ccbs-do-processo` — a CCB do caso e as dos casos eletrônicos da mesma pasta
 *   e processo para onde quem envia pode encaminhar;
 * - `POST /api/casos/{id}/recebimentos/{recebimento}/destinos` — depois de selado, o recebimento do
 *   complemento vai, pelos mesmos bytes, a cada CCB escolhida (um recebimento novo em cada caso, com a
 *   origem). Encaminhar de novo ao mesmo caso não repete.
 */
import axios, { type AxiosInstance } from 'axios';

export interface CcbIrmaData {
  case_id: string;
  codigo: string;
  ccb: string;
}

export interface CcbsDoProcessoData {
  /** O número da CCB deste caso; nulo no caso sem CCB declarada. */
  ccb: string | null;
  irmas: CcbIrmaData[];
}

export interface DestinoDoComplementoData {
  case_id: string;
  codigo: string;
  recebimento_id: string;
  documentos: number;
}

export interface ComplementoEncaminhadoData {
  destinos: DestinoDoComplementoData[];
}

export type ComplementoErrorReason =
  | 'aberto'
  | 'fora_do_processo'
  | 'nao_encontrado'
  | 'network'
  | 'unknown';

export class ComplementoError extends Error {
  public readonly reason: ComplementoErrorReason;

  constructor(reason: ComplementoErrorReason) {
    super(reason);
    this.reason = reason;
    this.name = 'ComplementoError';
  }
}

const POR_DETALHE: Readonly<Record<string, ComplementoErrorReason>> = {
  RECEBIMENTO_ABERTO: 'aberto',
  DESTINO_FORA_DO_PROCESSO: 'fora_do_processo',
  CASE_NOT_FOUND: 'nao_encontrado',
  RECEBIMENTO_NOT_FOUND: 'nao_encontrado',
};

function motivo(err: unknown): ComplementoErrorReason {
  if (!axios.isAxiosError(err)) return 'unknown';
  if (err.response === undefined) return 'network';
  const detalhe = (err.response.data as { detail?: unknown } | undefined)?.detail;
  return typeof detalhe === 'string' ? (POR_DETALHE[detalhe] ?? 'unknown') : 'unknown';
}

export async function lerCcbsDoProcesso(
  client: AxiosInstance,
  caseId: string,
): Promise<CcbsDoProcessoData> {
  try {
    return (await client.get<CcbsDoProcessoData>(`/casos/${caseId}/ccbs-do-processo`)).data;
  } catch (err) {
    throw new ComplementoError(motivo(err));
  }
}

export async function encaminharComplemento(
  client: AxiosInstance,
  caseId: string,
  recebimentoId: string,
  casos: readonly string[],
): Promise<ComplementoEncaminhadoData> {
  try {
    const { data } = await client.post<ComplementoEncaminhadoData>(
      `/casos/${caseId}/recebimentos/${recebimentoId}/destinos`,
      { casos },
    );
    return data;
  } catch (err) {
    throw new ComplementoError(motivo(err));
  }
}
